import { useLang } from 'hooks/lang';

import { FullClientPackage } from 'entities';

import { Button, Card, Table } from 'components/Blocks';
import { formatCurrency } from 'utils/text';
import { getTimestampDisplay } from 'utils/date';
import { OpenFolderIcon } from 'components/Icons';
import { compareStrings } from 'utils/array';

interface PackageTableProps {
  children?: never;
  loading?: boolean;
  packages: FullClientPackage[];

  dateRef?: Extract<keyof FullClientPackage, 'createdAt' | 'canceledAt'>;
}

export function PackageTable({ packages, loading, dateRef = 'createdAt' }: PackageTableProps) {
  const { t } = useLang();

  return (
    <Card>
      <Table
        items={packages}
        loading={loading}
        uniqueKey="id"
        columns={[
          {
            label: t('client') as string,
            property: 'clientName',
            filter: true,
            sort: true,
          },
          {
            label: t('name') as string,
            property: 'name',
            search: true,
            sort: true,
          },
          {
            label: t('value') as string,
            getTextValue: ({ currency }) => currency,
            render: ({ value, currency }) => formatCurrency(value, currency),
            filter: true,
            sort: true,
            sorter: (a, b) => {
              const [aMonetary, bMonetary] = [a, b].map(({ currency, amountAvailable }) => ({
                currency,
                value: amountAvailable,
              }));

              if (aMonetary.currency !== bMonetary.currency)
                return aMonetary.currency.localeCompare(bMonetary.currency);

              const [valueA, valueB] = [aMonetary.value, bMonetary.value].map(Number);

              return valueB - valueA;
            },
          },
          {
            label: t('amount-available') as string,
            filter: true,
            sort: true,

            sorter: (a, b) => {
              const [aMonetary, bMonetary] = [a, b].map(({ currency, value }) => ({
                currency,
                value,
              }));

              if (aMonetary.currency !== bMonetary.currency)
                return aMonetary.currency.localeCompare(bMonetary.currency);

              const [valueA, valueB] = [aMonetary.value, bMonetary.value].map(Number);

              return valueA - valueB;
            },

            getTextValue: ({ currency }) => currency,
            render: ({ amountAvailable, currency }) => formatCurrency(amountAvailable, currency),
          },
          {
            label: dateRef === 'createdAt' ? t('created-at') : t('canceled-at'),
            render: (pkg) => getTimestampDisplay(pkg[dateRef]!),
            sort: true,
            sorter: (a, b) => compareStrings(a[dateRef]!, b[dateRef]!),
          },

          {
            label: t('projects') as string,
            className: 'w-[120px]',
            filter: true,
            sort: true,
            hide: dateRef === 'canceledAt',

            sorter: (a, b) => {
              const [countA, countB] = [a, b].map(({ projects }) => projects.length);

              return countA - countB;
            },

            render: ({ projects }) => `${projects.length}`,
          },

          {
            render: (single) => (
              <Button variant="ghost" to="packageProfile" params={{ packageId: single.id }}>
                <OpenFolderIcon />
                {t('access')}
              </Button>
            ),

            className: 'w-[200px] text-center',
          },
        ]}
      />
    </Card>
  );
}
